import {
  Badge,
  Banner,
  Button,
  Dialog,
  Empty,
  Loader,
  Select,
  Surface,
  Switch,
  Table,
  Tabs,
  Text,
} from '@cloudflare/kumo'
import {
  ArrowDown,
  ArrowsClockwise,
  ArrowUp,
  ChatCircle,
  Eraser,
  Eye,
  EyeSlash,
  PushPin,
} from '@phosphor-icons/react'
import {
  CASE_COUNTRY_LABELS,
  formatCaseMoney,
  LANGUAGE_NAMES,
  LANGUAGES,
  taxonomyLabel,
  type AdminCaseStatus,
  type AdminCaseView,
  type AppSettings,
  type Language,
} from '@repo/shared'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { adminApi, type AdminComment } from '../api'
import { casesApi } from '../cases-api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { env } from '../env'
import { useGetToken } from '../lib/auth'
import {
  compareCaseOrder,
  galleryLine,
  planRankMove,
  type RankChange,
  type RankMove,
} from '../lib/case-order'
import { fmtInt, fmtTime, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'

const ALL = 'all'
type LangTab = typeof ALL | Language
type StatusFilter = typeof ALL | AdminCaseStatus

const STATUS_LABEL: Record<AdminCaseStatus, string> = {
  visible: '보임',
  hidden: '숨김',
  deleted: '내림',
}
const STATUS_BADGE: Record<AdminCaseStatus, 'success' | 'warning' | 'error'> = {
  visible: 'success',
  hidden: 'warning',
  deleted: 'error',
}
/** 표에서 상태 묶음 순서. 보이는 사례를 먼저 */
const STATUS_RANK: Record<AdminCaseStatus, number> = { visible: 0, hidden: 1, deleted: 2 }

/** 나라 이름 (샘플번호 앞 2자). 표는 shared 한 벌(한국어 값). 표에 없는 코드는 코드 그대로 보인다 (설계 4.2) */
const countryLabel = (code: string) => {
  const name = CASE_COUNTRY_LABELS[code]?.ko
  return name ? `${name} (${code})` : code
}

/** 글 사진과 같은 공개 경로 (로그인 없이, 1년 캐시) */
const imageUrl = (key: string) => `${env.API_URL}/community/images/${key}`

const langOrder = (l: string) => {
  const i = (LANGUAGES as readonly string[]).indexOf(l)
  return i < 0 ? LANGUAGES.length : i
}

/** 확인 창이 필요한 켜고 끄기 (숨김·보이기·시작 허용) */
type ToggleKind = 'hide' | 'show' | 'start-off' | 'start-on'
interface PendingToggle {
  kind: ToggleKind
  item: AdminCaseView
}

const TOGGLE_TEXT: Record<
  ToggleKind,
  { title: string; body: string; confirm: string; destructive: boolean; done: string }
> = {
  hide: {
    title: '이 사례를 갤러리에서 숨길까요?',
    body: '앱 갤러리와 상세 화면에서 빠져요. 1단계 커뮤니티 글은 그대로예요. 언제든 다시 보이게 할 수 있어요.',
    confirm: '숨기기',
    destructive: true,
    done: '숨겼어요. 앱에는 1분 안에 반영돼요.',
  },
  show: {
    title: '이 사례를 갤러리에 다시 보일까요?',
    body: '설정에서 갤러리가 켜져 있으면 모든 사용자에게 1분 안에 보여요. 손님에게도 보여요.',
    confirm: '보이기',
    destructive: false,
    done: '다시 보이게 했어요. 앱에는 1분 안에 반영돼요.',
  },
  'start-off': {
    title: '이 사례의 시작하기를 막을까요?',
    body: "상세 화면에서 '이 사례로 시작하기' 버튼이 사라져요. 이미 만든 제품은 그대로예요.",
    confirm: '시작 막기',
    destructive: true,
    done: '시작하기를 막았어요.',
  },
  'start-on': {
    title: '이 사례의 시작하기를 허용할까요?',
    body: '사용자가 이 사례로 제품을 만들 수 있어요. 시작 1번은 생성 1회를 쓰고 AI 비용이 들어요. 설정의 시작하기 허용이 꺼져 있으면 계속 막혀 있어요.',
    confirm: '시작 허용',
    destructive: false,
    done: '시작하기를 허용했어요.',
  },
}

const RANK_DONE: Record<RankMove, string> = {
  up: '한 칸 올렸어요.',
  down: '한 칸 내렸어요.',
  pin: '순위 줄 끝에 넣었어요.',
  clear: '순위를 지웠어요. 엑셀 순서로 돌아가요.',
}

/**
 * 완성 사례 (2단계 갤러리, 설계 7장). 엑셀로 넣은 사례를 앱 갤러리에 보일지, 시작하기를 허용할지, 어떤 순서로 둘지 정한다.
 * 내용 고치기·만들기·영구 삭제는 없다. 내용의 원본은 엑셀이다.
 * 바꾸는 동작은 한 번에 하나만 보낸다 (연타 잠금). 숨김·보이기·시작 허용은 확인 창을 거친다.
 */
export function CasesPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [items, setItems] = useState<AdminCaseView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  /** 갤러리 설정 (설정 화면의 '완성 사례'). undefined = 아직 못 읽음, null = 이 서버에 칸이 없음 */
  const [gallery, setGallery] = useState<AppSettings['sampleCases'] | null | undefined>(undefined)

  const [lang, setLang] = useState<LangTab>(ALL)
  const [country, setCountry] = useState<string>(ALL)
  const [status, setStatus] = useState<StatusFilter>(ALL)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      setItems(await casesApi.listAll(getToken))
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setLoading(false)
    }
  }, [getToken])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    adminApi
      .getSettings(getToken)
      .then(({ settings }) => setGallery(settings.sampleCases ?? null))
      .catch(() => setGallery(null))
  }, [getToken])

  // ── 바꾸기: 한 번에 하나만 (연타 잠금). ref 는 같은 틀 안의 두 번째 클릭까지 막는다 ──
  const lock = useRef(false)
  const [pending, setPending] = useState<string | null>(null)
  const mutate = async (caseId: string, work: () => Promise<void>, done: string) => {
    if (lock.current) return false
    lock.current = true
    setPending(caseId)
    setError(null)
    setNotice(null)
    let ok = false
    try {
      await work()
      setNotice(done)
      ok = true
    } catch (err) {
      setError(toMessage(err))
    } finally {
      // 실패해도 서버 상태로 다시 맞춘다 (순위 2건 중 1건만 된 경우 등)
      await reload()
      lock.current = false
      setPending(null)
    }
    return ok
  }

  const [toggle, setToggle] = useState<PendingToggle | null>(null)
  const applyToggle = async () => {
    if (!toggle) return
    const { kind, item } = toggle
    const patch =
      kind === 'hide' || kind === 'show'
        ? { hidden: kind === 'hide' }
        : { startEnabled: kind === 'start-on' }
    await mutate(
      item.id,
      async () => {
        await casesApi.patch(getToken, item.id, patch)
      },
      `${item.sampleNo} ${TOGGLE_TEXT[kind].done}`,
    )
    setToggle(null)
  }

  const moveRank = (item: AdminCaseView, move: RankMove) => {
    if (!items) return
    const changes: RankChange[] = planRankMove(items, item.id, move)
    if (!changes.length) return
    void mutate(
      item.id,
      async () => {
        // 차례로 보낸다. 두 건이면 자리 바꾸기다
        for (const ch of changes) {
          await casesApi.patch(getToken, ch.id, { adminRank: ch.adminRank })
        }
      },
      `${item.sampleNo} ${RANK_DONE[move]}`,
    )
  }

  // ── 거르기 ──
  const langCounts = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of items ?? []) m.set(c.language, (m.get(c.language) ?? 0) + 1)
    return m
  }, [items])

  const inLang = useMemo(
    () => (items ?? []).filter((c) => lang === ALL || c.language === lang),
    [items, lang],
  )

  const countryItems = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of inLang) m.set(c.country, (m.get(c.country) ?? 0) + 1)
    const sorted = [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    return [
      { label: '나라: 전체', value: ALL },
      ...sorted.map(([code, n]) => ({ label: `${countryLabel(code)} ${n}`, value: code })),
    ]
  }, [inLang])

  const statusItems = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of inLang) m.set(c.status, (m.get(c.status) ?? 0) + 1)
    return [
      { label: '상태: 전체', value: ALL },
      ...(['visible', 'hidden', 'deleted'] as const).map((s) => ({
        label: `상태: ${STATUS_LABEL[s]} ${m.get(s) ?? 0}`,
        value: s,
      })),
    ]
  }, [inLang])

  const rows = useMemo(
    () =>
      inLang
        .filter((c) => country === ALL || c.country === country)
        .filter((c) => status === ALL || c.status === status)
        .sort(
          (a, b) =>
            langOrder(a.language) - langOrder(b.language) ||
            STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
            compareCaseOrder(a, b),
        ),
    [inLang, country, status],
  )

  /** 언어별 갤러리 줄에서의 자리 (1부터) 와 그 줄의 순위 있는 사례 수 */
  const lines = useMemo(() => {
    const pos = new Map<string, number>()
    const rankedCount = new Map<string, number>()
    for (const l of new Set((items ?? []).map((c) => c.language))) {
      const line = galleryLine(items ?? [], l)
      line.forEach((c, i) => pos.set(c.id, i + 1))
      rankedCount.set(l, line.filter((c) => c.adminRank !== null).length)
    }
    return { pos, rankedCount }
  }, [items])

  const paged = usePagedList(items === null ? null : rows)
  const { setPage } = paged
  // 거르기를 바꾸면 첫 쪽으로
  useEffect(() => setPage(1), [lang, country, status, setPage])

  const locked = pending !== null

  // ── 글 열기 (댓글 보기, 읽기만) ──
  const [opened, setOpened] = useState<AdminCaseView | null>(null)
  const [comments, setComments] = useState<AdminComment[] | null>(null)
  const [commentsError, setCommentsError] = useState<string | null>(null)
  const openPost = (c: AdminCaseView) => {
    setOpened(c)
    setComments(null)
    setCommentsError(null)
    adminApi.community
      .comments(getToken, c.postId)
      .then(({ items: list }) => setComments(list))
      .catch((err: unknown) => setCommentsError(toMessage(err)))
  }

  const langTabs = [
    { value: ALL, label: `전체 ${items?.length ?? 0}` },
    ...LANGUAGES.map((l) => ({
      value: l,
      label: `${LANGUAGE_NAMES[l]} ${langCounts.get(l) ?? 0}`,
    })),
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="완성 사례"
        description="엑셀로 넣은 예시 사례예요(운영팀이 만든 예시, 실제 제품 아님). 앱 갤러리에 보일지, 시작하기를 허용할지, 순서를 정해요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={loading}
            disabled={locked}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />

      <HelpPanel
        id="cases"
        steps={[
          <>
            위쪽 <b>언어</b> 탭과 <b>나라</b>·<b>상태</b> 로 사례를 거르세요.
          </>,
          <>
            <b>숨기기</b> 를 누르면 앱 갤러리에서만 빠져요. 1단계 커뮤니티 글은 그대로예요.
          </>,
          <>
            <b>시작 허용</b> 을 끄면 그 사례의 &apos;이 사례로 시작하기&apos; 버튼이 사라져요.
          </>,
          <>
            <b>순위</b> 는 언어마다 따로예요. 핀을 누르면 순위 줄 끝에 들어가고, 위·아래로 자리를
            바꿔요. 지우개를 누르면 엑셀 순서로 돌아가요.
          </>,
        ]}
        notes={[
          '갤러리 전체를 켜고 끄는 스위치는 [설정] 화면의 완성 사례 칸에 있어요.',
          '앱은 바뀐 값을 1분 안에 읽어요.',
          '시작은 사례에서 시작한 제품 수(실패 제외), 완료는 끝까지 만든 제품 수예요. 앞은 최근 30일, 뒤는 전체예요.',
          '바꾼 것은 모두 [작업 기록] 에 남아요.',
        ]}
      />

      <Banner
        variant="secondary"
        size="sm"
        title="내용의 원본은 엑셀이에요."
        description="사진, 가격, 재료, 만드는 법을 고치려면 엑셀을 고쳐 다시 넣으세요. 여기서는 숨김, 시작 허용, 순위만 바꿔요."
      />

      <GalleryStatus gallery={gallery} onOpenSettings={() => void navigate('/settings')} />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}
      {notice && !error && <Banner variant="default" size="sm" title={notice} />}

      <div className="flex flex-col gap-3">
        <Tabs
          variant="underline"
          size="sm"
          value={lang}
          onValueChange={(v) => setLang(v as LangTab)}
          tabs={langTabs}
        />
        <div className="flex flex-wrap gap-2">
          <Select
            aria-label="나라"
            size="sm"
            className="min-w-40"
            value={country}
            onValueChange={(v: string | null) => setCountry(v ?? ALL)}
            items={countryItems}
          />
          <Select
            aria-label="상태"
            size="sm"
            className="min-w-36"
            value={status}
            onValueChange={(v: string | null) => setStatus((v ?? ALL) as StatusFilter)}
            items={statusItems}
          />
        </div>
      </div>

      <Surface className="overflow-x-auto rounded-lg p-0">
        {items === null ? (
          error ? (
            <Empty title="목록을 읽지 못했어요" description="위 오류를 확인하고 새로고침하세요." />
          ) : (
            <div className="flex items-center gap-3 p-6">
              <Loader />
              <Text variant="secondary">불러오는 중…</Text>
            </div>
          )
        ) : items.length === 0 ? (
          <Empty
            title="아직 사례가 없어요"
            description="가져오기 도구로 엑셀을 넣으면 여기에 나타나요."
          />
        ) : rows.length === 0 ? (
          <Empty title="조건에 맞는 사례가 없어요" description="거르기를 바꿔 보세요." />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row className="whitespace-nowrap">
                <Table.Head>사례</Table.Head>
                <Table.Head>언어 · 나라</Table.Head>
                <Table.Head>분류</Table.Head>
                <Table.Head>가격 (엑셀 통화)</Table.Head>
                <Table.Head>사진</Table.Head>
                <Table.Head>글</Table.Head>
                <Table.Head>
                  시작 · 완료
                  <div className="text-kumo-subtle text-xs font-normal">30일 / 전체</div>
                </Table.Head>
                <Table.Head>상태</Table.Head>
                <Table.Head>시작 허용</Table.Head>
                <Table.Head>순위</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paged.slice.map((c) => (
                <CaseRow
                  key={c.id}
                  c={c}
                  position={lines.pos.get(c.id) ?? null}
                  rankedInLine={lines.rankedCount.get(c.language) ?? 0}
                  locked={locked}
                  busy={pending === c.id}
                  onToggle={(kind) => setToggle({ kind, item: c })}
                  onMove={(move) => moveRank(c, move)}
                  onOpenPost={() => openPost(c)}
                />
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <TablePagination
        page={paged.page}
        setPage={paged.setPage}
        perPage={paged.perPage}
        total={paged.total}
      />

      <ConfirmDialog
        open={toggle !== null}
        title={toggle ? TOGGLE_TEXT[toggle.kind].title : ''}
        description={
          toggle ? (
            <>
              <span className="font-medium">
                {toggle.item.sampleNo} {toggle.item.name}
              </span>
              <br />
              {TOGGLE_TEXT[toggle.kind].body}
            </>
          ) : (
            ''
          )
        }
        confirmLabel={toggle ? TOGGLE_TEXT[toggle.kind].confirm : ''}
        destructive={toggle ? TOGGLE_TEXT[toggle.kind].destructive : false}
        busy={locked}
        onConfirm={() => void applyToggle()}
        onClose={() => !locked && setToggle(null)}
      />

      <Dialog.Root open={opened !== null} onOpenChange={(o) => !o && setOpened(null)}>
        <Dialog size="lg" className="max-h-[90vh] overflow-y-auto p-6">
          <Dialog.Title className="text-lg font-semibold">
            {opened ? `${opened.sampleNo} ${opened.name}` : ''}
          </Dialog.Title>
          <Dialog.Description className="text-kumo-subtle mt-1">
            1단계 커뮤니티 글의 댓글이에요. 읽기만 해요. 지우기·추천은 [커뮤니티] 메뉴에서 해요.
          </Dialog.Description>
          {opened && (
            <div className="mt-4 flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {opened.postAlive ? (
                  <Badge variant="success">글 게시 중</Badge>
                ) : (
                  <Badge variant="error">글 지워짐</Badge>
                )}
                <span className="text-kumo-subtle font-mono text-xs">{opened.postId}</span>
              </div>
              {commentsError && (
                <Banner variant="error" title="댓글을 읽지 못했어요" description={commentsError} />
              )}
              {comments === null && !commentsError ? (
                <div className="flex items-center gap-3">
                  <Loader />
                  <Text variant="secondary">불러오는 중…</Text>
                </div>
              ) : comments && comments.length === 0 ? (
                <Text variant="secondary">댓글이 없어요.</Text>
              ) : (
                comments?.map((cm) => (
                  <div key={cm.id} className="border-kumo-line rounded-lg border p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium">{cm.authorName}</span>
                      <span className="text-kumo-subtle">{fmtTime(cm.createdAt)}</span>
                      {cm.isDeleted && <Badge variant="error">삭제됨</Badge>}
                    </div>
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm">{cm.body}</p>
                  </div>
                ))
              )}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => void navigate('/community')}>
              커뮤니티 메뉴로
            </Button>
            <Button variant="primary" onClick={() => setOpened(null)}>
              닫기
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  )
}

/** 앱 갤러리 스위치 상태 한 줄 (바꾸는 곳은 설정 화면) */
function GalleryStatus({
  gallery,
  onOpenSettings,
}: {
  gallery: AppSettings['sampleCases'] | null | undefined
  onOpenSettings: () => void
}) {
  if (gallery === undefined) return null
  const action = (
    <Button variant="secondary" size="sm" onClick={onOpenSettings}>
      설정으로
    </Button>
  )
  if (gallery === null) {
    return (
      <Banner
        variant="alert"
        size="sm"
        title="이 서버는 아직 갤러리 설정을 모르는 버전이에요."
        description="서버를 배포한 뒤 다시 보세요."
      />
    )
  }
  if (!gallery.enabled) {
    return (
      <Banner
        variant="alert"
        size="sm"
        title="앱 갤러리가 꺼져 있어요."
        description="여기서 바꾼 것은 설정에서 갤러리를 켠 뒤에 앱에 보여요."
        action={action}
      />
    )
  }
  return (
    <Banner
      variant="default"
      size="sm"
      title="앱 갤러리가 켜져 있어요."
      description={
        gallery.startEnabled
          ? '바꾼 것은 1분 안에 앱에 보여요.'
          : '시작하기는 설정에서 모든 사례가 막혀 있어요.'
      }
      action={action}
    />
  )
}

function CaseRow({
  c,
  position,
  rankedInLine,
  locked,
  busy,
  onToggle,
  onMove,
  onOpenPost,
}: {
  c: AdminCaseView
  /** 앱 갤러리 줄에서의 자리. 숨김·내림이면 null */
  position: number | null
  /** 같은 언어 갤러리 줄에서 순위가 있는 사례 수 */
  rankedInLine: number
  locked: boolean
  busy: boolean
  onToggle: (kind: ToggleKind) => void
  onMove: (move: RankMove) => void
  onOpenPost: () => void
}) {
  const cat = taxonomyLabel(c.axis, c.majorId, c.subId, 'ko')
  const ranked = c.adminRank !== null
  // 순위 줄 안에서의 자리: 순위 있는 사례는 갤러리 줄 맨 앞에 모여 있다
  const first = position === 1
  const lastRanked = position !== null && position === rankedInLine
  const inGallery = c.status === 'visible'

  return (
    <Table.Row data-testid="case-row">
      <Table.Cell>
        <div className="flex min-w-56 items-center gap-3">
          {/* 사진 없는 템플릿은 빈 칸 (앱은 분류 사진을 쓴다) */}
          {c.thumbKey ? (
            <img
              src={imageUrl(c.thumbKey)}
              alt=""
              loading="lazy"
              className="bg-kumo-fill size-12 shrink-0 rounded-md object-cover"
            />
          ) : (
            <div className="bg-kumo-fill size-12 shrink-0 rounded-md" />
          )}
          <div className="min-w-0">
            <div className="max-w-60 truncate font-medium">{c.name}</div>
            <div className="text-kumo-subtle font-mono text-xs">{c.sampleNo}</div>
            {c.tagline && (
              <div className="text-kumo-subtle max-w-60 truncate text-xs">{c.tagline}</div>
            )}
            {/* 셰프 크레딧: 확인됨(레시피)·메뉴면 이름과 동의 문서 위치를 보인다 (동의 문서·레시피 출처는 관리자에게만) */}
            {c.chefCreditStatus !== 'none' ? (
              <div className="mt-1 flex max-w-60 flex-col gap-0.5 text-xs">
                <Badge variant="success">
                  {c.chefCreditStatus === 'verified' ? '셰프 레시피 확인됨' : '셰프 메뉴'} ·{' '}
                  {c.chefName?.ko ?? '-'}
                </Badge>
                <span className="text-kumo-subtle truncate">
                  동의 문서: {c.chefConsentRef ?? '-'}
                </span>
                <span className="text-kumo-subtle truncate">
                  레시피 출처: {c.chefRecipeSource ?? '-'} · 셰프 사진{' '}
                  {c.chefPhotoKey ? (c.chefPhotoCredit ? '있음(라이선스)' : '있음') : '없음'}
                </span>
                {c.chefSource && (
                  <a
                    className="text-kumo-link truncate underline"
                    href={c.chefSource.url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    원문 링크 ({c.chefSource.kind})
                  </a>
                )}
              </div>
            ) : (
              <div className="text-kumo-subtle mt-1 text-xs">셰프 크레딧 없음</div>
            )}
            {c.creditedImageCount > 0 && (
              <div className="text-kumo-subtle text-xs">
                사진 저작권 표시 {c.creditedImageCount}장
              </div>
            )}
          </div>
        </div>
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">
        <div>{LANGUAGE_NAMES[c.language]}</div>
        <div className="text-kumo-subtle text-xs">{countryLabel(c.country)}</div>
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">
        <div>{cat.major}</div>
        <div className="text-kumo-subtle text-xs">
          {[cat.sub, c.format].filter(Boolean).join(' · ')}
        </div>
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">
        {/* 국가별 요리 템플릿은 숫자가 없다 (API 가 null 을 준다) */}
        {c.sourceRetail === null || c.sourceUnitCost === null || c.marginPct === null ? (
          <Badge variant="outline">템플릿 · 숫자 없음</Badge>
        ) : (
          <>
            <div>{formatCaseMoney(c.sourceRetail, c.sourceCurrency, 'ko')}</div>
            <div className="text-kumo-subtle text-xs">
              1개 원가 {formatCaseMoney(c.sourceUnitCost, c.sourceCurrency, 'ko')}
            </div>
            <Badge variant={c.marginPct > 0 ? 'outline' : 'warning'}>마진 {c.marginPct}%</Badge>
          </>
        )}
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">{c.imageCount}장</Table.Cell>
      <Table.Cell>
        <div className="flex flex-col items-start gap-1">
          {c.kind === 'template' ? (
            <Badge variant="outline">글 없음</Badge>
          ) : c.postAlive ? (
            <Badge variant="success">게시 중</Badge>
          ) : (
            <Badge variant="error">지워짐</Badge>
          )}
          {c.kind !== 'template' && (
            <Button
              variant="ghost"
              size="sm"
              icon={<ChatCircle />}
              aria-label={`${c.sampleNo} 글 열기`}
              onClick={onOpenPost}
            >
              열기
            </Button>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">
        <Stat label="시작" value={c.starts} />
        <Stat label="완료" value={c.completed} />
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">
        <div className="flex flex-col items-start gap-1">
          <Badge variant={STATUS_BADGE[c.status]}>{STATUS_LABEL[c.status]}</Badge>
          {c.isDeleted ? (
            <Text variant="secondary" size="xs">
              엑셀에서 내림
            </Text>
          ) : c.isHidden ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<Eye />}
              disabled={locked}
              onClick={() => onToggle('show')}
            >
              보이기
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              icon={<EyeSlash />}
              disabled={locked}
              onClick={() => onToggle('hide')}
            >
              숨기기
            </Button>
          )}
        </div>
      </Table.Cell>
      <Table.Cell>
        {c.isDeleted ? (
          <Text variant="secondary" size="sm">
            없음
          </Text>
        ) : (
          <Switch
            aria-label={`${c.sampleNo} 시작 허용`}
            checked={c.startEnabled}
            disabled={locked}
            onCheckedChange={(checked: boolean) => onToggle(checked ? 'start-on' : 'start-off')}
          />
        )}
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap">
        <RankCell
          c={c}
          position={position}
          ranked={ranked}
          inGallery={inGallery}
          first={first}
          lastRanked={lastRanked}
          locked={locked}
          busy={busy}
          onMove={onMove}
        />
      </Table.Cell>
    </Table.Row>
  )
}

function Stat({ label, value }: { label: string; value: { last30d: number; total: number } }) {
  return (
    <div className="text-sm">
      <span className="text-kumo-subtle">{label}</span> {fmtInt(value.last30d)} /{' '}
      {fmtInt(value.total)}
    </div>
  )
}

function RankCell({
  c,
  position,
  ranked,
  inGallery,
  first,
  lastRanked,
  locked,
  busy,
  onMove,
}: {
  c: AdminCaseView
  position: number | null
  ranked: boolean
  inGallery: boolean
  first: boolean
  lastRanked: boolean
  locked: boolean
  busy: boolean
  onMove: (move: RankMove) => void
}) {
  const label: ReactNode = (
    <div className="text-xs">
      {position !== null ? (
        <span>갤러리 {position}번째</span>
      ) : (
        <span className="text-kumo-subtle">갤러리에 없음</span>
      )}
      <span className="text-kumo-subtle"> · {ranked ? `순위 ${c.adminRank}` : '엑셀 순서'}</span>
    </div>
  )
  if (c.isDeleted) return label
  return (
    <div className="flex flex-col gap-1">
      {label}
      <div className="flex gap-1">
        {ranked ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              shape="square"
              icon={<ArrowUp />}
              aria-label={`${c.sampleNo} 위로`}
              disabled={locked || !inGallery || first}
              loading={busy}
              onClick={() => onMove('up')}
            />
            <Button
              variant="ghost"
              size="sm"
              shape="square"
              icon={<ArrowDown />}
              aria-label={`${c.sampleNo} 아래로`}
              disabled={locked || !inGallery || lastRanked}
              onClick={() => onMove('down')}
            />
            <Button
              variant="ghost"
              size="sm"
              shape="square"
              icon={<Eraser />}
              aria-label={`${c.sampleNo} 순위 지우기`}
              disabled={locked}
              onClick={() => onMove('clear')}
            />
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            icon={<PushPin />}
            aria-label={`${c.sampleNo} 순위 주기`}
            disabled={locked}
            loading={busy}
            onClick={() => onMove('pin')}
          >
            순위 주기
          </Button>
        )}
      </div>
    </div>
  )
}
